import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import Modal from "@/components/Modal";
import { Field, SummaryStat } from "@/pages/Income";
import { Plus, Pencil, Trash2, TrendingUp } from "lucide-react";
import MoneyBurst from "@/components/MoneyBurst";
import GoalCompleteModal from "@/components/GoalCompleteModal";
import DateField from "@/components/DateField";

export default function Goals() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [contribOpen, setContribOpen] = useState(null);
  const [contribAmount, setContribAmount] = useState("");
  const [contribBurst, setContribBurst] = useState(0);
  const [completedGoal, setCompletedGoal] = useState(null); // goal just crossed 100 %
  const [form, setForm] = useState({ name:"", target_amount:"", saved_amount:"0", deadline:"", monthly_contribution:"", priority:"medium", auto_amount:"", auto_day:"" });

  const load = useCallback(async () => { const { data } = await api.get("/goals"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEdit(null); setForm({ name:"", target_amount:"", saved_amount:"0", deadline:"", monthly_contribution:"", priority:"medium", auto_amount:"", auto_day:"" }); setOpen(true); };
  const startEdit = (i) => { setEdit(i); setForm({ ...i, monthly_contribution: i.monthly_contribution ?? "", auto_amount: i.auto_amount ?? "", auto_day: i.auto_day ?? "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, target_amount: parseFloat(form.target_amount),
      saved_amount: parseFloat(form.saved_amount || 0),
      deadline: form.deadline, priority: form.priority,
      monthly_contribution: form.monthly_contribution === "" ? null : parseFloat(form.monthly_contribution),
      auto_amount: form.auto_amount === "" ? null : parseFloat(form.auto_amount),
      auto_day: form.auto_day === "" ? null : parseInt(form.auto_day, 10),
    };
    try {
      if (edit) await api.put(`/goals/${edit.id}`, payload); else await api.post("/goals", payload);
      toast.success(t("common.toast_saved")); setOpen(false); load();
    } catch (e) { console.error("Goal save failed:", e); toast.error(t("common.toast_failed")); }
  };
  const runAutoNow = async (goal) => {
    try {
      const { data } = await api.post(`/goals/${goal.id}/run-auto-contribution`);
      if (data.applied > 0) {
        toast.success(`Auto-contribute · added ${formatMoney(data.applied, cur)}`);
        load();
      } else {
        toast(data.reason || "Nothing to contribute right now");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run auto-contribution");
    }
  };
  const addContrib = async () => {
    const amt = parseFloat(contribAmount);
    if (!amt) return;
    const g = contribOpen;
    const prevSaved = Number(g.saved_amount) || 0;
    const target = Number(g.target_amount) || 0;
    const nextSaved = prevSaved + amt;
    const justCompleted = target > 0 && prevSaved < target && nextSaved >= target;
    const payload = { ...g, saved_amount: nextSaved };
    await api.put(`/goals/${g.id}`, payload);
    toast.success(`Added ${formatMoney(amt, user?.currency)} to ${g.name}`);
    setContribBurst(b => b + 1); // fire the small +$ burst
    setTimeout(() => {
      setContribOpen(null);
      setContribAmount("");
      load();
      if (justCompleted) {
        // Slight delay so the modal opens right after the contribution modal closes,
        // giving the small burst a moment to be seen first.
        setTimeout(() => setCompletedGoal({ ...g, saved_amount: nextSaved }), 250);
      }
    }, 350);
  };
  const remove = async (id) => { if (!confirm("Delete goal?")) return; await api.delete(`/goals/${id}`); toast.success(t("common.toast_deleted")); load(); };

  const cur = user?.currency || "USD";
  const totalTarget = items.reduce((a,b) => a + Number(b.target_amount||0), 0);
  const totalSaved = items.reduce((a,b) => a + Number(b.saved_amount||0), 0);

  return (
    <div>
      <PageHeader
        subtitle={t("pages.goals.subtitle")}
        title={t("pages.goals.title")}
        action={<button className="btn-primary" onClick={startNew} data-testid="add-goal-btn"><Plus size={16}/> {t("pages.goals.add")}</button>}
      />

      <div className="grid md:grid-cols-3 gap-5 mb-6 stagger">
        <SummaryStat label={t("pages.goals_body.active_goals")} value={items.length}/>
        <SummaryStat label={t("pages.goals_body.total_target")} value={formatMoney(totalTarget, cur)}/>
        <SummaryStat label={t("pages.goals_body.total_saved")} value={formatMoney(totalSaved, cur)}/>
      </div>

      {items.length === 0 ? (
        <EmptyState title={t("pages.goals.empty_title")} body={t("pages.goals_body.empty_body")}
          action={<button className="btn-primary" onClick={startNew}><Plus size={16}/> {t("pages.goals_body.add_goal")}</button>}/>
      ) : (
        <div className="grid md:grid-cols-2 gap-5 stagger">
          {items.map(g => {
            const pct = Math.max(0, Math.min(100, (g.saved_amount / g.target_amount) * 100));
            const remaining = g.target_amount - g.saved_amount;
            let monthsLeft = 1;
            try {
              const d = new Date(g.deadline); const now = new Date();
              monthsLeft = Math.max(1, (d.getFullYear() - now.getFullYear()) * 12 + (d.getMonth() - now.getMonth()));
            } catch (e) { console.warn("Goals: date parse failed for", g.deadline, e); }
            const requiredMonthly = remaining / monthsLeft;
            const priTone = g.priority === "high" ? "badge-danger" : g.priority === "medium" ? "badge-warn" : "badge-neutral";
            const priLabel = t(`pages.goals_body.pri_${g.priority}`, { defaultValue: g.priority });
            return (
              <div key={g.id} className="card-flat p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-lg font-medium" style={{fontFamily:'var(--font-heading)'}}>{g.name}</div>
                    <div className="text-xs text-[var(--text-muted)]">{t("pages.goals_body.by_deadline", { date: g.deadline, months: monthsLeft })}</div>
                  </div>
                  <span className={`badge ${priTone}`}>{priLabel}</span>
                </div>
                <div className="mt-5 flex items-end justify-between">
                  <div className="num-lg">{formatMoney(g.saved_amount, cur)}</div>
                  <div className="text-sm text-[var(--text-muted)]">{t("pages.goals_body.of_target", { amount: formatMoney(g.target_amount, cur) })}</div>
                </div>
                <div className="mt-3">
                  <div className="h-2 rounded-full bg-[var(--bg-accent)] overflow-hidden">
                    <div className="h-full bg-[var(--accent-primary)]" style={{ width: `${pct}%` }}/>
                  </div>
                  <div className="text-xs text-[var(--text-muted)] mt-1.5">{t("pages.goals_body.funded_pct", { pct: pct.toFixed(1) })}</div>
                </div>
                <div className="mt-4 p-3 rounded-lg bg-[var(--bg-accent)] text-sm">
                  <div className="text-[var(--text-secondary)]">{t("pages.goals_body.required_monthly")}</div>
                  <div className="num-md mt-0.5">{formatMoney(requiredMonthly, cur)}</div>
                </div>
                {g.auto_amount > 0 && g.auto_day ? (
                  <div
                    className="mt-3 flex items-center justify-between gap-2 text-xs px-2.5 py-1.5 rounded-md"
                    style={{ background: "rgba(224,158,80,0.10)", color: "#A16A25" }}
                    data-testid={`auto-badge-${g.id}`}
                  >
                    <span>
                      {t("pages.goals_body.auto_badge", { amount: formatMoney(g.auto_amount, cur), day: g.auto_day })}
                    </span>
                    <button
                      type="button"
                      onClick={() => runAutoNow(g)}
                      className="font-semibold hover:underline"
                      data-testid={`run-auto-${g.id}`}
                    >
                      {t("pages.goals_body.run_now")}
                    </button>
                  </div>
                ) : null}
                <div className="mt-4 flex justify-between">
                  <button className="btn-ghost" onClick={() => { setContribOpen(g); setContribAmount(""); }} data-testid={`contrib-goal-${g.id}`}><TrendingUp size={15}/> {t("pages.goals_body.add_contribution")}</button>
                  <div>
                    <button className="btn-danger-ghost" onClick={() => startEdit(g)}><Pencil size={15}/></button>
                    <button className="btn-danger-ghost" onClick={() => remove(g.id)}><Trash2 size={15}/></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? t("pages.goals_body.edit_title") : t("pages.goals_body.add_title")}>
        <form onSubmit={save} className="grid gap-4">
          <Field label={t("pages.goals_body.goal_name")}><input required className="field-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder={t("pages.goals_body.goal_name_p")} data-testid="input-goal-name"/></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("pages.goals_body.target_l", { cur })}><input type="number" step="0.01" required className="field-input" value={form.target_amount} onChange={e => setForm({...form, target_amount: e.target.value})} data-testid="input-goal-target"/></Field>
            <Field label={t("pages.goals_body.already_saved", { cur })}><input type="number" step="0.01" className="field-input" value={form.saved_amount} onChange={e => setForm({...form, saved_amount: e.target.value})}/></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("pages.goals_body.deadline")}><DateField value={form.deadline} onChange={v => setForm({...form, deadline: v})} testid="input-goal-deadline"/></Field>
            <Field label={t("pages.goals_body.priority")}>
              <select className="field-input" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
                {["low","medium","high"].map(x => <option key={x} value={x}>{t(`pages.goals_body.pri_${x}`)}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t("pages.goals_body.monthly_contribution_opt", { cur })}><input type="number" step="0.01" className="field-input" value={form.monthly_contribution} onChange={e => setForm({...form, monthly_contribution: e.target.value})}/></Field>

          <div
            className="rounded-lg border p-3.5"
            style={{ borderColor: "var(--border-default)", background: "var(--bg-accent)" }}
          >
            <div className="label-eyebrow mb-2" data-testid="auto-contrib-section">{t("pages.goals_body.auto_contribute")}</div>
            <div className="text-xs text-[var(--text-secondary)] mb-3 leading-relaxed">
              {t("pages.goals_body.auto_contribute_hint")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("pages.goals_body.amount_l", { cur })}>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="field-input"
                  value={form.auto_amount}
                  onChange={e => setForm({...form, auto_amount: e.target.value})}
                  placeholder="e.g. 100"
                  data-testid="input-goal-auto-amount"
                />
              </Field>
              <Field label={t("pages.goals_body.day_of_month")}>
                <input
                  type="number"
                  min="1"
                  max="28"
                  className="field-input"
                  value={form.auto_day}
                  onChange={e => setForm({...form, auto_day: e.target.value})}
                  placeholder="e.g. 1"
                  disabled={!form.auto_amount}
                  data-testid="input-goal-auto-day"
                />
              </Field>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
            <button type="submit" className="btn-primary" data-testid="submit-goal-btn">{edit ? t("common.save") : t("common.add")}</button>
          </div>
        </form>
      </Modal>

      <Modal open={!!contribOpen} onClose={() => setContribOpen(null)} title={t("pages.goals_body.add_to_title", { name: contribOpen?.name || "" })}>
        <div className="grid gap-4">
          <Field label={t("pages.goals_body.contrib_amount", { cur })}><input type="number" step="0.01" className="field-input" value={contribAmount} onChange={e => setContribAmount(e.target.value)} autoFocus data-testid="input-contrib-amount"/></Field>
          <div className="flex justify-end gap-2 relative">
            <MoneyBurst trigger={contribBurst} tone="pos" />
            <button className="btn-ghost" onClick={() => setContribOpen(null)}>{t("common.cancel")}</button>
            <button className="btn-primary" onClick={addContrib} data-testid="submit-contrib-btn">{t("common.add")}</button>
          </div>
        </div>
      </Modal>

      {/* Goal-complete celebration — fires when a contribution pushes a goal
          from <target to ≥target for the first time. */}
      {completedGoal && (
        <GoalCompleteModal
          goal={completedGoal}
          currency={cur}
          onClose={() => setCompletedGoal(null)}
        />
      )}
    </div>
  );
}
